import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";

const input = "mt-1 w-full rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-forest";
const label = "text-xs font-semibold uppercase tracking-[0.15em] text-moss";
const btn = "rounded-md bg-forest px-4 py-2 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50";

const EMPTY = { name: "", category: "Aesthetics", price: 0, interval: "month", description: "", perks: [""], badge: "", footnote: "", active: true };

function PlanEditor({ plan, isNew, onDone }) {
  const [d, setD] = useState({ ...EMPTY, ...plan, perks: plan?.perks?.length ? plan.perks : [""] });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setD((x) => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...d, price: Number(d.price), perks: d.perks.filter((p) => p.trim()) };
      if (isNew) await api.post("/admin/membership-plans", payload);
      else await api.put(`/admin/membership-plans/${plan.id}`, payload);
      toast.success(isNew ? "Plan created" : "Plan saved");
      onDone(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${plan.name}"?`)) return;
    await api.delete(`/admin/membership-plans/${plan.id}`);
    toast.success("Plan deleted");
    onDone(true);
  };

  return (
    <form data-testid="plan-editor" onSubmit={save} className="rounded-lg border border-linen bg-ivory p-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div><label className={label}>Plan name</label><input data-testid="plan-name-input" required value={d.name} onChange={set("name")} className={input} /></div>
        <div><label className={label}>Category</label>
          <select data-testid="plan-category-input" value={d.category} onChange={set("category")} className={input}>
            {["Aesthetics", "Weight Loss", "Women's Hormones", "Men's Hormones", "Wellness"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div><label className={label}>Price ($)</label><input data-testid="plan-price-input" type="number" min="0" required value={d.price} onChange={set("price")} className={input} /></div>
        <div><label className={label}>Billing</label>
          <select data-testid="plan-interval-input" value={d.interval} onChange={set("interval")} className={input}>
            <option value="month">per month</option><option value="quarter">per quarter</option><option value="year">per year</option>
          </select>
        </div>
      </div>
      <div className="mt-4"><label className={label}>Short description</label><textarea data-testid="plan-description-input" rows={2} value={d.description} onChange={set("description")} className={input} /></div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div><label className={label}>Badge (optional, e.g. "Most Popular")</label><input data-testid="plan-badge-input" maxLength={30} value={d.badge || ""} onChange={set("badge")} className={input} /></div>
        <div><label className={label}>Footnote (optional)</label><input data-testid="plan-footnote-input" maxLength={120} value={d.footnote || ""} onChange={set("footnote")} placeholder="Medication & labs billed separately" className={input} /></div>
      </div>
      <p className={`mt-4 ${label}`}>What's included</p>
      <div className="mt-1 space-y-2">
        {d.perks.map((p, i) => (
          <div key={i} className="flex gap-2">
            <input data-testid={`plan-perk-${i}`} value={p} onChange={(e) => setD((x) => ({ ...x, perks: x.perks.map((y, j) => (j === i ? e.target.value : y)) }))} className="w-full rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-forest" />
            <button type="button" onClick={() => setD((x) => ({ ...x, perks: x.perks.filter((_, j) => j !== i) }))} className="text-moss hover:text-destructive" aria-label="Remove perk"><Trash2 size={15} /></button>
          </div>
        ))}
        <button type="button" data-testid="plan-add-perk" onClick={() => setD((x) => ({ ...x, perks: [...x.perks, ""] }))} className="flex items-center gap-1.5 text-sm font-semibold text-forest"><Plus size={14} /> Add perk</button>
      </div>
      <label className="mt-4 flex items-center gap-2 text-sm text-forest"><input data-testid="plan-active-input" type="checkbox" checked={d.active} onChange={set("active")} className="h-4 w-4 accent-forest" /> Show on website</label>
      <div className="mt-5 flex items-center gap-3 border-t border-linen pt-4">
        <button type="submit" data-testid="plan-save-button" disabled={busy} className={btn}>{busy ? "Saving..." : isNew ? "Create Plan" : "Save Plan"}</button>
        <button type="button" onClick={() => onDone(false)} className="text-sm font-semibold text-moss hover:text-forest">Cancel</button>
        {!isNew && <button type="button" data-testid="plan-delete-button" onClick={remove} className="ml-auto text-sm font-semibold text-destructive/80 hover:text-destructive">Delete</button>}
      </div>
    </form>
  );
}

export default function MembershipsTab() {
  const [plans, setPlans] = useState([]);
  const [requests, setRequests] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => Promise.all([
    api.get("/admin/membership-plans").then((r) => setPlans(r.data)),
    api.get("/admin/membership-requests").then((r) => setRequests(r.data)),
  ]), []);
  useEffect(() => { load(); }, [load]);

  const decide = async (r, status) => {
    try {
      await api.patch(`/admin/membership-requests/${r.id}`, { status });
      toast.success(status === "active" ? `${r.name}'s membership is now active` : "Request declined");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const open = requests.filter((r) => r.status === "requested");

  return (
    <div data-testid="memberships-tab" className="space-y-10">
      <section>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl font-medium text-forest">Membership Requests</h2>
            <p className="mt-1 text-sm text-moss">{open.length} waiting. Activate after collecting payment at the salon.</p>
          </div>
        </div>
        {requests.length === 0 ? <p className="mt-6 text-sm text-moss">No requests yet.</p> : (
          <div className="mt-5 overflow-x-auto rounded-lg border border-linen bg-ivory">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-linen text-xs uppercase tracking-[0.1em] text-moss"><tr><th className="px-4 py-3">Client</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Requested</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
              <tbody className="divide-y divide-linen">
                {requests.map((r) => (
                  <tr key={r.id} data-testid={`membership-request-row-${r.id}`}>
                    <td className="px-4 py-3"><p className="font-medium text-forest">{r.name}</p><p className="text-xs text-moss">{r.email}{r.phone ? ` · ${r.phone}` : ""}</p>{r.note && <p className="mt-1 text-xs italic text-moss">“{r.note}”</p>}</td>
                    <td className="px-4 py-3 text-forest">{r.plan_name}<p className="text-xs text-moss">${r.plan_price}</p></td>
                    <td className="px-4 py-3 text-moss">{r.created_at.slice(0, 10)}</td>
                    <td className="px-4 py-3"><span className={`rounded-md border px-2 py-0.5 text-[11px] font-semibold uppercase ${r.status === "active" ? "border-forest/30 bg-forest/10 text-forest" : r.status === "declined" ? "border-destructive/30 text-destructive" : "border-linen text-moss"}`}>{r.status}</span></td>
                    <td className="px-4 py-3 text-right">
                      {r.status === "requested" && (
                        <div className="flex justify-end gap-2">
                          <button data-testid={`membership-request-activate-${r.id}`} onClick={() => decide(r, "active")} className="rounded-md bg-forest px-3 py-1.5 text-xs font-semibold text-ivory hover:bg-forest-dark">Paid — Activate</button>
                          <button data-testid={`membership-request-decline-${r.id}`} onClick={() => decide(r, "declined")} className="rounded-md border border-linen px-3 py-1.5 text-xs font-semibold text-moss hover:border-forest hover:text-forest">Decline</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-serif text-3xl font-medium text-forest">Membership Plans</h2>
            <p className="mt-1 text-sm text-moss">These appear on the website. Edit pricing and perks any time.</p>
          </div>
          <button data-testid="plan-new-button" onClick={() => setEditing("new")} className={`${btn} flex items-center gap-2`}><Plus size={15} /> New plan</button>
        </div>
        {editing === "new" && <div className="mt-5"><PlanEditor isNew onDone={(saved) => { setEditing(null); if (saved) load(); }} /></div>}
        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {plans.map((p) => editing === p.id ? (
            <div key={p.id} className="md:col-span-2 xl:col-span-3"><PlanEditor plan={p} onDone={(saved) => { setEditing(null); if (saved) load(); }} /></div>
          ) : (
            <article key={p.id} data-testid={`plan-card-${p.id}`} className={`rounded-lg border border-linen bg-ivory p-5 ${!p.active ? "opacity-60" : ""}`}>
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gold-dark">{p.category}{p.badge ? ` · ${p.badge}` : ""}{p.is_placeholder ? " · sample" : ""}{!p.active ? " · hidden" : ""}</p>
              <h3 className="mt-1 font-serif text-xl font-medium text-forest">{p.name}</h3>
              <p className="mt-1 font-serif text-3xl text-forest">${p.price}<span className="font-sans text-xs text-moss">/{p.interval}</span></p>
              <ul className="mt-3 space-y-1 text-xs text-moss">{p.perks.map((k) => <li key={k}>• {k}</li>)}</ul>
              <button data-testid={`plan-edit-${p.id}`} onClick={() => setEditing(p.id)} className="mt-4 text-sm font-semibold text-forest hover:underline">Edit</button>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
